import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Switch, Route, Redirect } from "react-router-dom";
import cx from "clsx";
import toastr from "toastr";
import { withErrorBoundary } from "react-error-boundary";
import { useAuth0 } from "@auth0/auth0-react";

import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";

import { makeStyles } from "@material-ui/core/styles";

import AdminNavbar from "components/Navbars/AdminNavbar.js";
import Footer from "components/Footer/Footer.js";
import Sidebar from "./Sidebar.js";

import AdminDashboard from "../components/admin/Dashboard";
import ChapterDashboard from "../components/chapter/Dashboard";
import NotFound from "../pages/NotFound";
import ErrorPage from "../pages/Error";

import routes from "../routes.js";
import { actionCreators as actionCreatorsUser } from "../_actions/userActions";

import styles from "assets/jss/material-dashboard-pro-react/layouts/adminStyle.js";
import sidebarImage from "../assets/img/sidebar-2.jpg";
import logoImage from "../assets/img/logo.png";
import logoMiniImage from "../assets/img/logo-white.svg";
import Unauthorized from "../components/Unauthorized.js";

var ps;

const useStyles = makeStyles(styles);

const Dashboard = props => {
  const { getUserInfo, user, error, ...rest } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [miniActive, setMiniActive] = useState(false);
  const classes = useStyles();
  const { logout, isAuthenticated, isLoading, getIdTokenClaims } = useAuth0();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      if (window.location.href.indexOf("401") > -1) {
        return;
      }
      getIdTokenClaims()
        .then(({ __raw: jwt }) => {
          const originalToken = localStorage.getItem("accToken");
          if (!originalToken || originalToken !== jwt) {
            localStorage.setItem("accToken", jwt);
            getUserInfo();
          }
        })
        .catch(err => {
          toastr.error(err.message);
          if (err.response.status === 401) {
            logout({
              returnTo: process.env.REACT_APP_RETURN_URL,
              federated: true
            });
          }
        });
    }
  }, [isLoading, isAuthenticated]);

  const mainPanel = React.createRef();
  useEffect(() => {
    if (navigator.platform.indexOf("Win") > -1) {
      ps = new PerfectScrollbar(mainPanel.current, {
        suppressScrollX: true,
        suppressScrollY: false
      });
      document.body.style.overflow = "hidden";
    }
    window.addEventListener("resize", resizeFunction);

    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1) {
        ps.destroy();
      }
      window.removeEventListener("resize", resizeFunction);
    };
  });

  useEffect(() => {}, []);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  const getUserStatus = useCallback(() => {
    return user
      ? user.role.isAdmin
        ? "Administration"
        : user.chapter.name
      : "";
  }, [user]);

  const sidebarMinimize = useCallback(() => {
    setMiniActive(!miniActive);
  }, [miniActive]);

  const resizeFunction = useCallback(() => {
    if (window.innerWidth >= 960) {
      setMobileOpen(false);
    }
  }, []);

  return (
    <div className={classes.wrapper}>
      <Sidebar
        routes={routes}
        logoMini={logoMiniImage}
        logo={logoImage}
        image={sidebarImage}
        handleDrawerToggle={handleDrawerToggle}
        open={mobileOpen}
        color="blue"
        bgColor="black"
        miniActive={miniActive}
        user={user}
        {...rest}
      />
      <div
        className={cx(classes.mainPanel, {
          [classes.mainPanelSidebarMini]: miniActive,
          [classes.mainPanelWithPerfectScrollbar]:
            navigator.platform.indexOf("Win") > -1
        })}
        style={{ minHeight: "50vh" }}
        ref={mainPanel}
      >
        <AdminNavbar
          sidebarMinimize={sidebarMinimize.bind(this)}
          miniActive={miniActive}
          user={user}
          brandText={getUserStatus()}
          handleDrawerToggle={handleDrawerToggle}
          {...rest}
        />
        {user && (
          <div className={classes.content}>
            <div className={classes.container}>
              {error ? (
                <ErrorPage error={error} />
              ) : (
                <Switch>
                  <Route path="/admin" component={AdminDashboard} />
                  <Route path="/chapter" component={ChapterDashboard} />
                  <Route exact path="/404" component={NotFound} />
                  <Redirect
                    from="/"
                    to={
                      user
                        ? user.role.isAdmin
                          ? "/admin"
                          : "/chapter"
                        : "/404"
                    }
                  />
                </Switch>
              )}
            </div>
          </div>
        )}
        {!user && (
          <Switch>
            <Route exact path="/401" component={Unauthorized} />
          </Switch>
        )}
        <Footer fluid />
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  return {
    isLoading: state.userReducer.isLoading,
    user: state.userReducer.user
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsUser }, dispatch);

const ReduxMountedDashboard = connect(
  mapStateToProps,
  mapDispatchToProps
)(Dashboard);

const ErrorBoundaryDashboard = withErrorBoundary(ReduxMountedDashboard, {
  FallbackComponent: ReduxMountedDashboard,
  onError(error, info) {
    console.log(error, info);
  }
});

export default ErrorBoundaryDashboard;
